import type { FC } from 'react'
import { useState } from 'react'
import styled from '@emotion/styled'

import { AppearanceProvider, useAppearance } from '@segment/matcha/foundations/AppearanceContext'

import * as CopyText from '@segment/matcha/recipes/CopyText'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import EmailCaptureForm from '@segment/templates/Forms/EmailCaptureForm'

import type { AppearanceProps } from '@segment/matcha/types'

import HeroBackground from './assets/Hero.svg'

const copy = {
  heading: `The State of Personalization Report 2024`,
  description: `By Twilio Segment`,
}

const form = {
  formId: `cnt-state-of-personalization-2024`,
  customTrackingType: `cnt-state-of-personalization-2024`,
  additionalTrackEvent: `Content Downloaded`,
  buttonText: `Get the full report`,
  successButtonText: `Download the report`,
  buttonUrl: `https://segment.com/pdfs/State-of-Personalization-Report-Twilio-Segment-2024.pdf`,
}

const Hero: FC<AppearanceProps> = ({ appearance: externalAppearance }) => {
  const appearance = useAppearance(externalAppearance)

  const [isFormSubmitted, setIsFormSubmitted] = useState(false)

  return (
    <AppearanceProvider appearance={appearance}>
      <Wrapper>
        <StyledHeroBase variant="2Col">
          <HeroRecipe.Copy>
            <CopyText.Heading variant="h1" tag="h1">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description variant="pExtraLarge" tag="p">
              {copy.description}
            </CopyText.Description>
            <HeroForm variant={isFormSubmitted ? `wide` : `narrow`}>
              <EmailCaptureForm
                customFormIdentifier={form.formId}
                customTrackingType={form.customTrackingType}
                additionalTrackEvent={form.additionalTrackEvent}
                resourceType="report"
                buttonText={form.buttonText}
                successButtonText={form.successButtonText}
                buttonUrl={form.buttonUrl}
                onSubmit={() => setIsFormSubmitted(true)}
                enableJobTitleField={true}
              />
            </HeroForm>
          </HeroRecipe.Copy>
        </StyledHeroBase>
        <Background>
          <HeroBackground width="100%" height="100%" />
        </Background>
      </Wrapper>
    </AppearanceProvider>
  )
}

const Wrapper = styled.header`
  background-color: ${({ theme }) => theme.palette.twilio.gray[100]};
  position: relative;
  z-index: 1;
`

const StyledHeroBase = styled(HeroBase)`
  ${HeroRecipe.Copy} {
    text-align: left;
  }
`

interface HeroFormProps {
  variant: `wide` | `narrow`
}

const HeroForm = styled(HeroRecipe.Form)<HeroFormProps>`
  max-width: ${({ variant }) => (variant === `narrow` ? `417px` : `unset`)};
`

const Background = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.gray[100]};
  position: absolute;
  top: -60px; // navbar height
  width: 100%;
  height: 100%;
  z-index: -1;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.up(`xxxl`)} {
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      width: 30%;
      height: 100%;
    }

    &::before {
      left: 0;
      background: linear-gradient(269.69deg, rgba(18, 28, 45, 0) 0.25%, #121c2d 60%);
    }

    &::after {
      right: 0;
      background: linear-gradient(-269.69deg, rgba(18, 28, 45, 0) 0.25%, #121c2d 60%);
    }
  }
`

export default Hero
